import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type S } from "@/shared/api/client";
import { date, num } from "@/shared/lib/format";
import { Facts } from "./Record";
import type { FieldSpec } from "./ResourceForm";

type Def = S["CustomFieldDef"];

/** The workspace's custom fields on an entity (core.organization, crm.lead, …), in order. */
export function useCustomDefs(entity?: string) {
  return useQuery({
    queryKey: ["/v1/core/custom-fields", entity],
    enabled: !!entity,
    staleTime: 60_000,
    queryFn: () => api.get<{ items: Def[] | null }>("/v1/core/custom-fields", { filter: { entity: entity! }, limit: 100 }),
    select: (r) => r.items ?? [],
  });
}

/** The custom fields as form fields, under custom.<key>. */
export function customSpecs(defs: Def[], lang: string): FieldSpec[] {
  return defs.map((d) => ({
    name: `custom.${d.key}`,
    label: (lang === "ar" ? d.label_ar : d.label_en) || d.label_ar,
    required: d.required,
    type: d.type === "number" ? "number" : d.type === "date" ? "date" : d.type === "bool" ? "bool" : d.type === "select" ? "select" : "text",
    options: d.type === "select" ? (d.options ?? []).map((o) => ({ value: o, label: o })) : undefined,
  }));
}

/** What a record has in the custom fields defined now: only those, as the API takes them. */
export function customBody(defs: Def[], values: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const d of defs) {
    const v = values?.[d.key];
    if (v !== undefined && v !== "") out[d.key] = v;
  }
  return out;
}

/** A record's custom fields with a value, under its details; nothing when it has none. */
export function CustomFacts({ entity, values }: { entity: string; values?: Record<string, unknown> | null }) {
  const { t, i18n } = useTranslation();
  const defs = useCustomDefs(entity).data ?? [];
  const shown = defs.filter((d) => values?.[d.key] !== undefined && values?.[d.key] !== null && values?.[d.key] !== "");
  if (shown.length === 0) return null;
  const text = (d: Def, v: unknown) => d.type === "bool" ? (v ? t("common.yes") : t("common.no")) : d.type === "date" ? date(String(v))
    : d.type === "number" ? num(v as number) : String(v);
  return <Facts title={t("custom.title")} items={shown.map((d) => [(i18n.language === "ar" ? d.label_ar : d.label_en) || d.label_ar, text(d, values?.[d.key])])} />;
}
